import { EventCard } from './event-card';
import {
  feedNow,
  isLiveRound,
  refreshDelay,
  roundOfMarket,
  toEventCardMarket,
  tradingRound,
} from './short-market-card-model';
import { prefetchRoute } from '@/client/confs/page-loaders';
import type { RoundFetch } from '@/client/pages/btc-5m/api';
import type { Token } from '@/client/pages/btc-5m/model';
import { normaliseEventId } from '@/client/pages/btc-5m/round-index';
import { useShortRoundsFeed } from '@/client/pages/btc-5m/use-rounds-feed';
import { useAppSettingStore } from '@/client/store/app-store';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';
import { keepPreviousData, type Query } from '@tanstack/react-query';
import useDocumentVisibility from 'ahooks/lib/useDocumentVisibility';
import { useEffect, useMemo, useState } from 'react';

// react-query pauses this while the tab is hidden (refetchIntervalInBackground stays false). After a failed
// refetch it keeps the last good feed, so the schedule goes on from that; without any feed it stops.
const refetchInterval = ({ state }: Query<RoundFetch>) =>
  state.data ? refreshDelay(state.data.rounds, feedNow(state.data, state.dataUpdatedAt)) : false;

// EventCard warms the event route on intent; a round opens the 5-minute page behind it, so warm that too.
const warm5mPage = () => prefetchRoute('btc-5m');

/**
 * First cell of the home grid: the BTC 5-minute round of the lens token as a standard EventCard (the
 * Polymarket model: the round looks like every other event; only the page behind it differs). It shows
 * the published round trading now, else the next published one, with a LIVE chip while that round trades.
 * Hidden without a feed (unset env var, or no good fetch yet), without a published round for the token,
 * or without its market detail (e.g. a 4xx). The 1s clock lives here; EventCard is memoised, so a tick
 * re-renders it only when LIVE flips.
 */
export const ShortMarketCard = ({ onShownChange }: { onShownChange?: (shown: boolean) => void } = {}) => {
  const token = useAppSettingStore(state => state.baseToken).symbol as Token;
  const { feed, updatedAt } = useShortRoundsFeed({ refetchInterval });
  const [now, setNow] = useState(() => feedNow(feed, updatedAt));
  useEffect(() => {
    if (!feed) return;
    const tick = () => setNow(feedNow(feed, updatedAt));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [feed, updatedAt]);

  const selected = feed ? tradingRound(feed.rounds, token, now) : undefined;
  // The 5-minute page's query (same key, same SSE refresh). Nothing loads while the tab is hidden. While
  // the next round's detail loads, react-query keeps the previous detail, shown with its own round.
  const hidden = useDocumentVisibility() === 'hidden';
  const { data: detail, isPlaceholderData } = useMarketDetail(
    api,
    { marketAddress: selected?.markets[token]?.marketAddress ?? '' },
    { enabled: !hidden, placeholderData: keepPreviousData }
  );
  const round = isPlaceholderData && detail && feed ? roundOfMarket(feed.rounds, token, detail.marketAddress) : selected;
  const eventId = normaliseEventId(round?.markets[token]?.eventId);
  const markets = useMemo(() => (detail && eventId ? [toEventCardMarket(detail, eventId)] : undefined), [detail, eventId]);
  // The home list hides its "no markets" state while this card is shown.
  const shown = !!(selected && round && markets);
  useEffect(() => onShownChange?.(shown), [shown, onShownChange]);
  useEffect(() => () => onShownChange?.(false), [onShownChange]);
  if (!selected || !round || !markets) return null;

  return (
    <div
      data-testid="short-market-home-card"
      className="flex"
      onMouseEnter={warm5mPage}
      onFocus={warm5mPage}
      onTouchStart={warm5mPage}
    >
      <EventCard eventId={eventId} markets={markets} index={0} live={isLiveRound(round, token, now)} />
    </div>
  );
};
